import type { ChangeEvent, ReactNode } from 'react';

interface BaseProps {
  name: string;
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
}

/** Wires label, control and error together with the right ARIA attributes. */
function Wrapper({
  name,
  label,
  error,
  required,
  className = '',
  children,
}: BaseProps & { children: ReactNode }) {
  return (
    <div className={className}>
      <label htmlFor={name} className="mb-2 block text-sm font-medium">
        {label}
        {/*
          Red, matching the error text below, and aria-hidden because it is
          decorative: the control itself carries `required`, so a screen reader
          already announces the field as required and would otherwise read a
          bare "star" after every label.
        */}
        {required && (
          <span className="ml-1 text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p id={`${name}-error`} role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// hover:border-ink/25 gives the field a response before it is focused — it
// previously changed only on focus, so a cursor over it got no acknowledgement.
const control =
  'w-full rounded-xl border bg-white px-4 py-3 text-base transition-colors duration-300 outline-none hover:border-ink/25 focus:border-brand focus:hover:border-brand min-h-12';

export function TextField({
  value,
  onChange,
  type = 'text',
  placeholder,
  ...rest
}: BaseProps & {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <Wrapper {...rest}>
      <input
        id={rest.name}
        name={rest.name}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        aria-invalid={!!rest.error}
        aria-describedby={rest.error ? `${rest.name}-error` : undefined}
        className={`${control} ${rest.error ? 'border-red-400' : 'border-hairline'}`}
      />
    </Wrapper>
  );
}

export function TextArea({
  value,
  onChange,
  rows = 6,
  placeholder,
  maxLength = 4000,
  ...rest
}: BaseProps & {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <Wrapper {...rest}>
      <textarea
        id={rest.name}
        name={rest.name}
        rows={rows}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!rest.error}
        aria-describedby={rest.error ? `${rest.name}-error` : undefined}
        className={`${control} resize-y ${rest.error ? 'border-red-400' : 'border-hairline'}`}
      />
      <p className="mt-1.5 text-right text-xs opacity-40">
        {value.length} / {maxLength}
      </p>
    </Wrapper>
  );
}

export function SelectField({
  value,
  onChange,
  options,
  ...rest
}: BaseProps & {
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <Wrapper {...rest}>
      <select
        id={rest.name}
        name={rest.name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!rest.error}
        aria-describedby={rest.error ? `${rest.name}-error` : undefined}
        className={`${control} ${rest.error ? 'border-red-400' : 'border-hairline'}`}
      >
        <option value="">Select one…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

export function CheckboxField({
  name,
  label,
  checked,
  onChange,
  error,
  required,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
  required?: boolean;
}) {
  return (
    <div>
      {/* Padding gives the label a 44px-tall hit area on touch. */}
      <label htmlFor={name} className="flex cursor-pointer items-start gap-3 py-2 text-sm">
        <input
          id={name}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-hairline accent-brand"
        />
        <span className="opacity-75">
          {label}
          {/* Matches Wrapper: red, and hidden from assistive tech. */}
          {required && (
            <span className="ml-1 text-red-600" aria-hidden="true">
              *
            </span>
          )}
        </span>
      </label>
      {error && (
        <p id={`${name}-error`} role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** Hidden honeypot. Bots fill it; humans never see it. */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
      <label htmlFor="website">Website</label>
      <input
        id="website"
        name="website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
